import { AuthContext } from 'contexts';
import { useContext, useEffect, useState } from 'react';
import type { SupraSubscriptionProductBillingModel } from '@bb/domain/dist/types';
import getOrgBillingModel from 'api/SupraController/getOrgBillingModel';
import { message } from '@bb/elements-web';

interface UseOrgBillingModelResult {
  /** Who is currently paying for the Supra subscription in this brokerage */
  supraSubscriptionPayer: SupraSubscriptionProductBillingModel;
  /** Whether we encountered an error fetching the org billing model */
  getOrgBillingModelError: boolean;
  /** Whether the hook is still fetching org billing model */
  loadingOrgBillingModel: boolean;
}

// Go grab who is in charge of paying for the Supra One subscription.
// We still fetch this at the parent level so it's shared across both
// section components (the card itself reads its own status from context).
const useOrgBillingModel = (): UseOrgBillingModelResult => {
  const { auth } = useContext(AuthContext);

  const hasSupraSubscriptionRecord = !!auth.supraSubscription;

  const [supraSubscriptionPayer, setSupraSubscriptionPayer] =
    useState<SupraSubscriptionProductBillingModel>(null);
  const [getOrgBillingModelError, setGetOrgBillingModelError] =
    useState<boolean>(false);
  const [loadingOrgBillingModel, setLoadingOrgBillingModel] =
    useState<boolean>(false);

  useEffect(() => {
    const fetchSupraSubscriptionPayer = async () => {
      if (hasSupraSubscriptionRecord) {
        setLoadingOrgBillingModel(true);
        try {
          setSupraSubscriptionPayer(
            await getOrgBillingModel({ orgId: auth.org.id }),
          );
        } catch {
          setGetOrgBillingModelError(true);
          message.error(
            'Error fetching org billing model for Supra subscription',
          );
        } finally {
          setLoadingOrgBillingModel(false);
        }
      }
    };
    fetchSupraSubscriptionPayer();
  }, [hasSupraSubscriptionRecord, auth.org.id]);

  return {
    supraSubscriptionPayer,
    getOrgBillingModelError,
    loadingOrgBillingModel,
  };
};

export default useOrgBillingModel;
